import Icons from "@/assets/icons";
import Button from "@/components/Button";
import Input from "@/components/Input";
import { ApiService } from "@/services/ApiService";
import { messageService } from "@/services/MessageService";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { validateFormikFields } from "@/lib/utils";
import { useEffect, useState } from "react";
import Select from "@/components/Select";
import RegisterUserSchema from "./Schema/RegisterUserSchema";

const RegisterUser = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [profileOptions, setProfileOptions] = useState([{}]);
  const [schema, setSchema] = useState(RegisterUserSchema(t));
  const { post, patch, get } = ApiService;
  const navigate = useNavigate();

  const initialValues = {
    login: "",
    nome: "",
    email: "",
    senha: "",
    perfil: "",
    status: "ATIVO",
  };

  const [apiData, setApiData] = useState<typeof initialValues>();

  const formikData = useFormik({
    initialValues,
    validate: (values) => validateFormikFields(values, schema),
    onSubmit: async (values) => {
      const request = id
        ? patch(`api/v1/usuario/atualizar/${id}`, values)
        : post("api/v1/usuario/registrar", values);

      const { status } = await request;

      if (status === 200) {
        const message = id
          ? {
              title: t("usersForm.messages.update.title"),
              detail: t("usersForm.messages.update.description"),
            }
          : {
              title: t("usersForm.messages.save.title"),
              detail: t("usersForm.messages.save.description"),
            };

        messageService.success(message.title, message.detail, () =>
          navigate("/usuario")
        );
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await get(`api/v1/usuario/buscarPorId/${id}`);
      setApiData(data);
    };

    if (id) {
      fetchData();
      setSchema(RegisterUserSchema(t, id));
    }
  }, [id]);

  useEffect(() => {
    if (apiData) {
      formikData.setValues({
        nome: apiData.nome ?? "",
        email: apiData.email ?? "",
        login: apiData.login ?? "",
        senha: "",
        perfil: apiData.perfil ?? "",
        status: apiData.status ?? "ATIVO",
      });
    }
  }, [apiData]);

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await get(`api/v1/perfil/listar`);
      const options = data.map((profile: any) => ({
        title: profile.nome,
        value: profile.nome,
      }));

      setProfileOptions(options.length ? options : [{}]);
    };

    fetchData();
  }, []);

  const statusOptions = [
    { title: t("table.user.body.active.true"), value: "ATIVO" },
    { title: t("table.user.body.active.false"), value: "INATIVO" },
  ];

  // Estilos padronizados
  const inputsFieldsetStyle =
    "bg-white rounded-lg border p-6 shadow-sm flex flex-col gap-6";
  const titleFieldsetStyle =
    "text-xl font-semibold text-gray-800 border-b pb-4 mb-4";
  const buttonsFieldsetStyle =
    "flex max-md:flex-wrap col-span-full justify-end gap-4";

  return (
    <div className="flex-1 p-8 max-w-7xl mx-auto">
      <div className="mb-8 border-b pb-4">
        <h1 className="text-3xl font-medium text-gray-800">
          {id ? t("usersForm.titleEdit") : t("usersForm.title")}
        </h1>
      </div>

      <form onSubmit={formikData.handleSubmit} className="flex flex-col gap-8">
        <fieldset className={inputsFieldsetStyle}>
          <h2 className={titleFieldsetStyle}>{t("usersForm.sections.main")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-6">
              <Input
                id="nome"
                label={t("usersForm.inputs.name.label")}
                error={
                  formikData.errors.nome && formikData.touched.nome
                    ? formikData.errors.nome
                    : ""
                }
                {...formikData.getFieldProps("nome")}
              />
            </div>
            <div className="md:col-span-6">
              <Input
                id="email"
                label={t("usersForm.inputs.email.label")}
                error={
                  formikData.errors.email && formikData.touched.email
                    ? formikData.errors.email
                    : ""
                }
                {...formikData.getFieldProps("email")}
              />
            </div>
            <div className="md:col-span-4">
              <Input
                id="login"
                label={t("usersForm.inputs.login.label")}
                error={
                  formikData.errors.login && formikData.touched.login
                    ? formikData.errors.login
                    : ""
                }
                {...formikData.getFieldProps("login")}
              />
            </div>
            
            {!id && (
              <div className="md:col-span-4">
                <Input
                  id="senha"
                  label={t("usersForm.inputs.password.label")}
                  type="password"
                  error={
                    formikData.errors.senha && formikData.touched.senha
                      ? formikData.errors.senha
                      : ""
                  }
                  {...formikData.getFieldProps("senha")}
                />
              </div>
            )}
            
            <div className="md:col-span-4">
              <Select
                id="perfil"
                label={t("usersForm.inputs.select.profile.label")}
                placeholder={t("usersForm.inputs.select.profile.placeholder")}
                options={profileOptions}
                error={
                  formikData.errors.perfil && formikData.touched.perfil
                    ? formikData.errors.perfil
                    : ""
                }
                {...formikData.getFieldProps("perfil")}
              />
            </div>
            
            <div className="md:col-span-4">
              <Select
                id="status"
                label={t("usersForm.inputs.active.label")}
                placeholder={t("usersForm.inputs.active.placeholder")}
                options={statusOptions}
                error={
                  formikData.errors.status && formikData.touched.status
                    ? (formikData.errors.status as string)
                    : ""
                }
                {...formikData.getFieldProps("status")}
                value={formikData.values.status}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className={buttonsFieldsetStyle}>
          <Button
            label={t("usersForm.buttons.clear")}
            color="neutro"
            onClick={() => formikData.resetForm()}
            icon={<Icons.Delete pathStyle="fill-inherit" />}
            type="button"
          />

          <Button
            label={t("usersForm.buttons.cancel")}
            color="neutro"
            onClick={() => navigate("/usuario")}
            icon={<Icons.Close pathStyle="fill-inherit" />}
            type="button"
          />

          <Button
            label={t("usersForm.buttons.save")}
            color="primario"
            disabled={formikData.isSubmitting}
            icon={<Icons.Save pathStyle="fill-inherit" />}
            type="submit"
          />
        </fieldset>
      </form>
    </div>
  );
};

export default RegisterUser;